import manifest from '@/generated/cms-image-manifest.json'

/**
 * A manifest entry. Older manifests stored a bare path string; entries are now
 * `{ src, srcSet }` so a responsive set can be described. Both shapes are read
 * so a stale `src/generated/cms-image-manifest.json` (checked in, regenerated
 * by `yarn optimize:cms-images`) degrades to the single-src behaviour instead of
 * breaking the build.
 */
type ImageEntry = string | { src: string; srcSet?: string }

const map = manifest as Record<string, ImageEntry>

const entryOf = (url?: string): ImageEntry | undefined => (url ? map[url] : undefined)

/**
 * Map a CMS `imageLink` URL to its build-optimized local WebP when the build
 * produced one; otherwise return the URL unchanged. The manifest is bundled
 * (imported, not fetched) so the server render and the post-hydration re-render
 * resolve to the SAME value — critical because seeded pages re-fetch CMS data
 * on mount and would otherwise clobber a data-layer rewrite.
 */
export function cmsImageSrc(url?: string): string | undefined {
  if (!url) return url
  const entry = entryOf(url)
  if (!entry) return url
  return typeof entry === 'string' ? entry : entry.src
}

/**
 * The `srcSet` for a CMS image, or `undefined` when the build emitted only one
 * width (small icons, or an image that was already optimal). Pair it with a
 * `sizes` attribute describing the image's RENDERED width, otherwise the browser
 * assumes 100vw and picks the widest candidate — which is the behaviour this is
 * meant to avoid on phones.
 */
export function cmsImageSrcSet(url?: string): string | undefined {
  const entry = entryOf(url)
  return typeof entry === 'object' ? entry.srcSet : undefined
}
